import { LocalNotifications } from '@capacitor/local-notifications';
import { Preferences } from '@capacitor/preferences';
import { esApp } from './plataforma';

/**
 * Recordatorios para apuntar gastos (solo la app de iOS, y solo con Finanzas).
 * Son notificaciones locales: las programa el propio iPhone, así que no hace
 * falta servidor ni conexión.
 *
 * - Inactividad: al abrir la app se programa un aviso para dentro de N días y
 *   se quita el anterior. Si se vuelve a entrar antes, no llega nunca.
 * - Diario: a la hora elegida. Se programan los próximos días uno a uno (no
 *   una repetición fija) para poder saltarse el de hoy si ya se ha apuntado algo.
 *
 * - Resumen del mes (Plus): el día 1 a las 10:00, un aviso para ver el
 *   resumen del mes que acaba de terminar en Informes.
 *
 * Los ajustes son de este iPhone (Preferences), como el permiso de iOS.
 */

export interface AjustesRecordatorios {
  inactividad: boolean;
  dias: number;
  diario: boolean;
  /** "HH:MM" */
  hora: string;
  /** Aviso el día 1 con el resumen del mes (solo con Plus). */
  resumenMes: boolean;
}

export const DIAS_INACTIVIDAD = [3, 5, 7];

const POR_DEFECTO: AjustesRecordatorios = { inactividad: false, dias: 3, diario: false, hora: '21:00', resumenMes: false };

const CLAVE_AJUSTES = 'recordatoriosGastos';
const CLAVE_ULTIMO_APUNTE = 'ultimoApunte';

/** El aviso de inactividad sale a esta hora del día que toque. */
const HORA_INACTIVIDAD = 20;
/** Días de recordatorio diario que se dejan programados (iOS admite 64 pendientes). */
const DIAS_PROGRAMADOS = 14;

const ID_INACTIVIDAD = 7000;
const ID_DIARIO = 7100;
const ID_RESUMEN = 7200;
const IDS = [ID_INACTIVIDAD, ID_RESUMEN, ...Array.from({ length: DIAS_PROGRAMADOS + 1 }, (_, i) => ID_DIARIO + i)];

const RUTA = '/finanzas/transacciones';
const RUTA_RESUMEN = '/finanzas/informes?seccion=resumen';
/** El aviso del resumen sale el día 1 a esta hora. */
const HORA_RESUMEN = 10;

/** Si tiene Plus: lo dice RecordatoriosGastos al reprogramar, y lo usan las demás llamadas. */
let conPlusGuardado = false;

export async function leerAjustes(): Promise<AjustesRecordatorios> {
  const { value } = await Preferences.get({ key: CLAVE_AJUSTES });
  if (!value) return POR_DEFECTO;
  try {
    return { ...POR_DEFECTO, ...JSON.parse(value) };
  } catch {
    return POR_DEFECTO;
  }
}

export type PermisoRecordatorios = 'concedido' | 'denegado' | 'preguntar';

export async function permisoRecordatorios(): Promise<PermisoRecordatorios> {
  const { display } = await LocalNotifications.checkPermissions();
  return display === 'granted' ? 'concedido' : display === 'denied' ? 'denegado' : 'preguntar';
}

/**
 * Guarda los ajustes y reprograma. Si se enciende alguno, pide permiso (la
 * primera vez sale el aviso de iOS): devuelve false si no lo da.
 */
export async function guardarAjustes(ajustes: AjustesRecordatorios): Promise<boolean> {
  if ((ajustes.inactividad || ajustes.diario || ajustes.resumenMes) && (await permisoRecordatorios()) !== 'concedido') {
    const { display } = await LocalNotifications.requestPermissions();
    if (display !== 'granted') return false;
  }
  await Preferences.set({ key: CLAVE_AJUSTES, value: JSON.stringify(ajustes) });
  await reprogramar(true);
  return true;
}

function hoy(): string {
  const d = new Date();
  return `${d.getFullYear()}-${d.getMonth() + 1}-${d.getDate()}`;
}

/** Al apuntar un movimiento: el recordatorio diario de hoy ya no hace falta. */
export async function alApuntar(): Promise<void> {
  if (!esApp) return;
  await Preferences.set({ key: CLAVE_ULTIMO_APUNTE, value: hoy() });
  await reprogramar(true);
}

/** Quita todo lo programado (al cerrar sesión, o si ya no tiene Finanzas). */
export async function cancelarRecordatorios(): Promise<void> {
  if (!esApp) return;
  await LocalNotifications.cancel({ notifications: IDS.map(id => ({ id })) });
}

/**
 * Vuelve a programar los avisos según los ajustes. Se llama al abrir la app
 * (por eso el de inactividad se aplaza solo) y al cambiar algo.
 */
export async function reprogramar(conFinanzas: boolean, conPlus: boolean = conPlusGuardado): Promise<void> {
  if (!esApp) return;
  conPlusGuardado = conPlus;
  await cancelarRecordatorios();
  const ajustes = await leerAjustes();
  const resumen = ajustes.resumenMes && conPlus;
  if (!conFinanzas || !(ajustes.inactividad || ajustes.diario || resumen)) return;
  if ((await permisoRecordatorios()) !== 'concedido') return;

  const ahora = new Date();
  const notificaciones = [];

  if (ajustes.inactividad) {
    const cuando = new Date(ahora);
    cuando.setDate(cuando.getDate() + ajustes.dias);
    cuando.setHours(HORA_INACTIVIDAD, 0, 0, 0);
    notificaciones.push({
      id: ID_INACTIVIDAD,
      title: '¿Apuntamos los gastos?',
      body: `Llevas ${ajustes.dias} días sin pasar por Finanzas. Ponerlo al día te lleva un minuto.`,
      schedule: { at: cuando, allowWhileIdle: true },
      extra: { ruta: RUTA },
    });
  }

  if (ajustes.diario) {
    const [h, m] = ajustes.hora.split(':').map(Number);
    const { value: ultimo } = await Preferences.get({ key: CLAVE_ULTIMO_APUNTE });
    const yaHoy = ultimo === hoy();
    for (let i = 0; i <= DIAS_PROGRAMADOS; i++) {
      const cuando = new Date(ahora);
      cuando.setDate(cuando.getDate() + i);
      cuando.setHours(h, m, 0, 0);
      if (cuando <= ahora || (i === 0 && yaHoy)) continue;
      notificaciones.push({
        id: ID_DIARIO + i,
        title: 'Apunta tus gastos',
        body: '¿Has gastado algo hoy? Apúntalo ahora y no se te olvida.',
        schedule: { at: cuando, allowWhileIdle: true },
        extra: { ruta: RUTA },
      });
    }
  }

  if (resumen) {
    const cuando = new Date(ahora.getFullYear(), ahora.getMonth() + 1, 1, HORA_RESUMEN, 0, 0, 0);
    const mes = ahora.toLocaleDateString('es-ES', { month: 'long' });
    notificaciones.push({
      id: ID_RESUMEN,
      title: `Tu resumen de ${mes}`,
      body: `Mira cuánto ahorraste en ${mes} y qué gastos se dispararon.`,
      schedule: { at: cuando, allowWhileIdle: true },
      extra: { ruta: RUTA_RESUMEN },
    });
  }

  if (notificaciones.length) await LocalNotifications.schedule({ notifications: notificaciones });
}
